import * as TweenJS from "slotworks/utils/tween";
import { Container, DisplayObject, Sprite } from "pixi.js";
import AnimatedSprite from "slotworks/graphics/animation/animated-sprite";
import { ButtonEvent } from "slotworks/graphics/elements/button-element";
import Layers from "slotworks/graphics/layers/layers";
import { CenterPivot, PIXIElement, Ungroup } from "slotworks/graphics/pixi/group";
import FreespinRequestPayload from "slotworks/model/gameplay/requests/freespin-request-payload";
import settingsModel from "slotworks/model/settings/settings-model";
import commsManager from "slotworks/server/comms-manager";
import HarleyComponents from "components/components";
import Contract from "slotworks/utils/contract";
import { sequence } from "slotworks/utils/race-utils";
import { startAnimation } from "./freespins-wheel-spins";
import Point from "slotworks/utils/geom/point";
import gameState from "slotworks/model/game-state";
import FreespinsWheelRecord from "integration/records/freespins-wheel-record";
import { BounceZoomIn, BounceZoomOut } from "slotworks/graphics/animation/base-animations";
import soundManager from "slotworks/sound/sound-manager";
import animationManager from "slotworks/graphics/animation/animation-manager";
import Timer from "slotworks/utils/timer";
import deviceInfo from "slotworks/utils/device-info";
import canvasManager, { Orientation } from "slotworks/graphics/canvas/canvas-manager";

const WHEEL_SPRITES = [
    "backing_light",
    "main_part",
    "wedge_5_bg_still",
    "blue5",
    "5",
    "5_light",
    "wedge_0_bg_still",
    "red0",
    "0",
    "0_light",
    "wedge_1_bg_still",
    "blue1",
    "1",
    "1_light",
    "wedge_2_bg_still",
    "red2",
    "2",
    "2_light",
    "wedge_3_bg_still",
    "blue3",
    "3",
    "3_light",
    "wedge_4_bg_still",
    "red4",
    "4",
    "4_light",
    "Goldpart",
    "neon_light_large",
];

// freespins_title, SelectedSpins15, SelectedSpins12, SelectedSpins10, SelectedSpins8, HandIcon, boosters, Boundary
const notPartOfTheWheel = [
    "freespins_title",
    "SelectedSpins15",
    "SelectedSpins12",
    "SelectedSpins10",
    "SelectedSpins8",
    "HandIcon",
    "boostersText",
    "Boundary"
];

export default class FreespinsWheelBoosters {

    public static ROTATION_TIME: number = 5500;

    private layer: Layers;
    private wheelContainer: Container;
    private wholeWheelContainer: Container;

    private hand: PIXI.Sprite;
    private handTween: TweenJS.Tween<PIXI.Sprite> = null;

    private activeAnimations: AnimatedSprite[];

    private segmentSize: number = 2 * Math.PI / 6; // 360 /sectors count

    private options: number[] = [0, 1, 2, 3, 4, 5];
    private selected: number; // index of options

    private sprites: DisplayObject[];


    private lastTime: number;
    private tickTimer: number;
    private spinResolve: () => void;
    private rotation: number = 0;
    private speed: number = 0;
    private maxSpeed: number = 0.01;
    private minSpeed: number = 0.0005;

    private state: number = 1; // 1 accel, 2 spin, 3 decel

    private accelTime = 1000;
    private spinTime = 2000;
    private decelTime = 5800;

    private stateTime = 0;

    private startRotation = 0;
    private targetRotation: number = 0;
    private notch: number = 0;

    constructor() {
        this.layer = Layers.Bonus;
    }

    public start() {
        Layers.BetBar.hide();
        Layers.SmartButton.hide();
        Layers.BonusForeground.getSprite("wheel2").visible = false;
        return sequence([
            () => this.zoomInWheel(),
            () => this.waitForInput(),
            () => this.spin(),
            () => this.celebrateWin(),
            () => this.complete(),
        ])
    }

    public end() {

    }

    public createWheel() {
        return new Contract(resolve => {
            this.layer.setScene("freespinswin2").execute();
            this.hand = this.layer.getSprite("HandIcon");
            this.hand.visible = false;

            this.hideSpins();

            const red0 = this.layer.getSprite("red0") as AnimatedSprite;
            startAnimation(red0);

            const red2 = this.layer.getSprite("red2") as AnimatedSprite;
            startAnimation(red2);
            copyValues(red0, red2);
            red2.anchor.set(-0.025, 1.14);
            red2.x += red2.width * red2.anchor.x;
            red2.y += red2.height * red2.anchor.y;
            red2.angle = 120;


            const red4 = this.layer.getSprite("red4") as AnimatedSprite;
            startAnimation(red4);
            copyValues(red0, red4);

            red4.anchor.set(-0.025, 1.14);
            red4.x += red4.width * red4.anchor.x;
            red4.y += red4.height * red4.anchor.y;
            red4.angle = -120;


            const blue5 = this.layer.getSprite("blue5") as AnimatedSprite;
            startAnimation(blue5);

            const blue1 = this.layer.getSprite("blue1") as AnimatedSprite;
            startAnimation(blue1);
            copyValues(blue5, blue1);

            blue1.anchor.set(1.02, 1.14);
            blue1.x += blue1.width * blue1.anchor.x;
            blue1.y += blue1.height * blue1.anchor.y;
            blue1.angle = 120;

            const blue3 = this.layer.getSprite("blue3") as AnimatedSprite;
            startAnimation(blue3);
            copyValues(blue5, blue3);

            blue3.anchor.set(1.02, 1.14);
            blue3.x += blue3.width * blue3.anchor.x;
            blue3.y += blue3.height * blue3.anchor.y;
            blue3.angle = -120;

            this.activeAnimations = [red0, blue1, red2, blue3, red4, blue5];

            const sprites = [];
            for (let s of WHEEL_SPRITES) {
                const sprite = this.layer.getSprite(s);
                sprites.push(sprite);
            }
            this.wheelContainer = CenterPivot(sprites, "boosters", new Point(0.50, 0.485));
            this.sprites = this.layer.container.children.slice();

            for (let i = 0; i <= 5; i++) {
                const light = this.wheelContainer.getChildByName(i + "_light");
                light.visible = false;
            }

            this.wholeWheelContainer = CenterPivot(this.sprites.filter(el => !notPartOfTheWheel.includes(el.name)) as PIXIElement[], "allboosters");
            const left = this.layer.container.children.slice();
            for (let c of left) {
                Layers.BonusForeground.add(c);
            }

            this.sprites = left;

            const startPosition = Layers.BonusForeground.getPosition("start");
            this.wholeWheelContainer.width = startPosition.width;
            this.wholeWheelContainer.height = startPosition.height;
            this.wholeWheelContainer.position.set(startPosition.x + startPosition.width * 0.5, startPosition.y + startPosition.height * 0.5);

            const record = gameState.getCurrentGame().getCurrentRecord() as FreespinsWheelRecord;
            const count = record.spinsWon;

            const spinsSprites = [
                Layers.BonusForeground.getSprite("freespins_title"),
                Layers.BonusForeground.getSprite("SelectedSpins" + count)
            ] as PIXIElement[];

            BounceZoomIn(spinsSprites, 1000, 300).execute();
            BounceZoomIn(Layers.BonusForeground.getSprite("boostersText"), 1000, 500).execute();

            soundManager.customEvent("bonus_music");

            resolve(null);
        });
    }

    private hideSpins() {
        const spins = [
            "SelectedSpins15",
            "SelectedSpins12",
            "SelectedSpins10",
            "SelectedSpins8"];
        for (let s of spins) {
            this.layer.getSprite(s).visible = false;
        }

        const record = gameState.getCurrentGame().getCurrentRecord() as FreespinsWheelRecord;
        const count = record.spinsWon;
        if (this.layer.getSprite("SelectedSpins" + count)) {
            this.layer.getSprite("SelectedSpins" + count).visible = true;
        } else
            console.error("*" + "SelectedSpins" + count + "*" + record.spinsWon)
    }

    public zoomInWheel() {
        return new Contract(resolve => {
            const record = gameState.getCurrentGame().getCurrentRecord();
            const recovery = false; // record instanceof FreespinsWheelRecord && record.spins > 0;

            const endPosition = Layers.BonusForeground.getPosition("end2");
            if (recovery) {
                this.wholeWheelContainer.scale.set(1, 1);
                this.wholeWheelContainer.x = endPosition.x + endPosition.width * 0.5;
                this.wholeWheelContainer.y = endPosition.y + endPosition.height * 0.5 + 30;
                resolve(null);
            } else {
                new TweenJS.Tween(this.wholeWheelContainer.scale)
                    .to({ x: 1, y: 1}, 500)
                    .onStart(() => soundManager.customEvent("free_spins_wheel_switch"))
                    .delay(400)
                    .start();

                new TweenJS.Tween(this.wholeWheelContainer)
                    .to({
                        x: endPosition.x + endPosition.width * 0.5,
                        y: [endPosition.y - 600, endPosition.y + endPosition.height * 0.5 + 30],
                        // width: endPosition.width,
                        // height: endPosition.height
                    },
                        500)
                    .delay(400)
                    .interpolation(TweenJS.Interpolation.Bezier)
                    // .easing(TweenJS.Easing.Quartic.Out)
                    .onComplete(() => {
                        // Ungroup(this.wholeWheelContainer, "allboosters");
                        resolve(null);
                    })
                    .start();
            }
        })
    }

    private waitForInput() {
        return new Contract(resolve => {
            const onKeyDown = (event: any) => {
                if (settingsModel.getSpaceToSpin()) {
                    if (event.keyCode === 32) {
                        onClick();
                    }
                }
            };

            const onClick = () => {
                document.removeEventListener("keyup", onKeyDown);
                document.body.removeEventListener(ButtonEvent.POINTER_DOWN.getEventString(), onClick, true);
                this.layer.container.buttonMode = false;
                this.layer.container.interactive = false;

                TweenJS.remove(this.handTween);
                this.handTween = null;
                this.hand.visible = false;


                commsManager.request(new FreespinRequestPayload()).then(() => {
                    const record = gameState.getCurrentGame().latestRecord() as FreespinsWheelRecord;
                    this.selected = this.options.indexOf(record.giftedAdditional);                    
                });
                resolve(null);
            };

            this.callToAction().then(() => {
                setTimeout(() => {
                    document.addEventListener("keyup", onKeyDown);
                    document.body.addEventListener(ButtonEvent.POINTER_DOWN.getEventString(), onClick, true);
                    this.layer.container.buttonMode = true;
                }, 100);
            });

            // 
        });
    }

    private callToAction() {
        return new Contract(resolve => {
            this.startHand();
            resolve(null);
        });
    }

    private startSpin() {
        this.rotation = this.wheelContainer.rotation;
        this.startRotation = this.rotation;

        this.state = 1;
        this.stateTime = 0;
        this.speed = this.minSpeed;

        this.startTick();
    }

    private startTick() {
        this.stopTick();
        this.tickTimer = Timer.setInterval(this.tick, 0);
        this.lastTime = Timer.time;
    }

    private stopTick() {
        Timer.clearInterval(this.tickTimer);
    }

    private tick = () => {
        this.update();
    }

    private update() {
        let difTime = Timer.time - this.lastTime;
        this.lastTime = Timer.time;
        this.stateTime += difTime;

        if (this.state === 1) {
            // ACCELERATION
            let t = this.stateTime / this.accelTime;

            if (t >= 1) {
                t = 1;
                this.state = 2;
                this.stateTime = 0;
            }

            this.speed = this.minSpeed + (this.maxSpeed - this.minSpeed) * t;
            this.rotation -= difTime * this.speed;
        } else if (this.state === 2) {
            // CONSTANT SPIN
            this.rotation -= difTime * this.maxSpeed;

            if (this.stateTime >= this.spinTime && this.selected > -1) {
                this.targetRotation =
                    this.rotation
                    - (Math.PI * 2 - Math.abs(this.rotation % (2 * Math.PI)))
                    - (Math.PI * 2 +
                        this.selected * this.segmentSize
                        + Math.PI / 6
                    );
                this.state = 3;
                this.stateTime = 0;
                this.startRotation = this.rotation;
            }
        } else if (this.state === 3) {
            // DECELERATION (EASE OUT)
            let t = this.stateTime / this.decelTime;

            if (t >= 1) {
                t = 1;
            }

            let eased = 1 - Math.pow(1 - t, 4);

            this.rotation =
                this.startRotation +
                (this.targetRotation - this.startRotation) * eased;

            if (t === 1) {
                this.stop();
            }
        }
        this.wheelContainer.rotation = this.rotation;
        this.updateNotch();
    }

    private stop() {
        this.stopTick();
        this.spinResolve();
        this.spinResolve = null;
        soundManager.customEvent("wheel_stop");
    }

    private celebrateWin() {
        return new Contract(resolve => {
            soundManager.customEvent("free_spins_selected_wedge");
            for (let anim of this.activeAnimations) {
                anim.stop();
                anim.visible = false;
            }

            const n = this.selected;

            // 1, 1_light
            const animKeys = {
                0: "red0",
                1: "blue1",
                2: "red2",
                3: "blue3",
                4: "red4",
                5: "blue5"
            }

            const anim = this.wheelContainer.getChildByName(animKeys[n as keyof typeof animKeys]) as AnimatedSprite;
            const winAnimation = animationManager.generateAnimation((n === 1 || n === 3 || n === 5) ? "Harley_Wheel_Blue_Wedge_BG_Win" : "Harley_Wheel_Red_Wedge_BG_Win");
            const sprite = this.wheelContainer.getChildByName(n.toString());
            const index = this.wheelContainer.getChildIndex(sprite);

            const light = this.wheelContainer.getChildByName(n + "_light");
            light.visible = true;

            this.wheelContainer.addChildAt(winAnimation, index - 1);


            const container = CenterPivot([light, sprite] as PIXIElement[]);

            let scaleTween = new TweenJS.Tween(container.scale)
                .to({ x: 1.3, y: 1.4 }, 600)
                .delay(100)
                .easing(TweenJS.Easing.Back.Out)
                .onComplete(() => {
                    scaleTween = new TweenJS.Tween(container.scale)
                    .to({x: 1, y: 1}, 280)
                    .delay(90)
                    .start();
                })
                .start();

            winAnimation.anchor.set(anim.anchor.x, anim.anchor.y);
            winAnimation.position.set(anim.x, anim.y);
            winAnimation.scale.set(anim.scale.x, anim.scale.y);
            winAnimation.angle = anim.angle;
            winAnimation.loop = true;
            winAnimation.gotoAndPlay(0);
            setTimeout(() => {
                if (scaleTween) {
                    scaleTween.stop();
                    TweenJS.remove(scaleTween);
                }
                winAnimation.stop();
                resolve(null);
            }, 1800);
        });
    }

    private spin() {
        return new Contract<void>(resolve => {
            // let targetRotation = 4 * Math.PI * 2 + this.selected * this.segmentSize + Math.PI / 6 + Math.random() * (Math.PI / 18);
            this.spinResolve = resolve;
            HarleyComponents.background.startBonusSpin();
            HarleyComponents.harley.spin2();
            if (!deviceInfo.isDesktop && canvasManager.orientation === Orientation.PORTRAIT) {
                setTimeout(() => {
                    soundManager.customEvent("wheel_spin");
                    this.rotation = this.wheelContainer.rotation;
                    this.startSpin();
                }, 200);
            } else {
                new TweenJS.Tween(this.wheelContainer)
                    .to({ rotation: Math.PI / 4 }, 600)
                    .delay(400)
                    .onComplete(() => {
                        setTimeout(() => {
                            soundManager.customEvent("wheel_spin");
                            this.rotation = this.wheelContainer.rotation;
                            this.startSpin();
                        }, 200);
                    })
                    .start();
            }
        });
    }

    private updateNotch() {
        const current = Math.floor(this.wheelContainer.angle / 60);
        if(this.notch != current) {
            soundManager.customEvent("wheel_spin_notch");
        }
        this.notch = current;
    }

    private startHand = () => {
        if (this.handTween) {
            this.handTween.stop();
            TweenJS.remove(this.handTween);
        }

        Layers.BonusForeground.container.removeChild(this.hand);
        Layers.BonusForeground.container.addChild(this.hand);

        const start = this.layer.localToLocal(Layers.BonusForeground, this.layer.getPosition("HandIcon2"));
        const end = this.layer.localToLocal(Layers.BonusForeground, this.layer.getPosition("HandIcon"));

        this.hand.visible = true;
        this.hand.position.set(start.x, start.y);
        this.handTween = new TweenJS.Tween(this.hand)
            .to({ x: end.x, y: end.y }, 800)
            .yoyo(true)
            .repeat(Infinity)
            .start();
    }

    private clearLayer() {
        for (let s of this.sprites) {
            Layers.BonusForeground.container.removeChild(s);
        }
    }

    private complete() {
        if (!this.wholeWheelContainer)
            return Contract.empty();
        return new Contract(resolve => {
            // zoom out

            const record = gameState.getCurrentGame().getCurrentRecord() as FreespinsWheelRecord;
            const count = record.spinsWon;

            const spinsSprites = [
                Layers.BonusForeground.getSprite("freespins_title"),
                Layers.BonusForeground.getSprite("SelectedSpins" + count)
            ] as PIXIElement[];

            BounceZoomOut(spinsSprites, 500).then(() => {
                for (let s of spinsSprites) {
                    s.visible = false;
                }
            });
            BounceZoomOut(Layers.BonusForeground.getSprite("boostersText"), 500).then(() => {
                Layers.BonusForeground.getSprite("boostersText").visible = false;
            });

            soundManager.customEvent("plaque_exit");
            new TweenJS.Tween(this.wholeWheelContainer)
                .to({ y: this.wholeWheelContainer.y + 800 }, 800)
                .onComplete(() => {
                    Ungroup(this.wholeWheelContainer, "allboosters");
                    this.clearLayer();
                    resolve(null);
                })
                .start();
        });
    }

}


const copyValues = (s: Sprite, s2: Sprite) => {
    s2.width = s.width;
    s2.height = s.height;
    s2.x = s.x;
    s2.y = s.y;
}      